import GroupedBarChart from "lib/components/charts/grouped-bar-chart";
import PractitionerRevenueChart from "lib/components/common/charts/practitioner-revenue.chart";
import RoomOccupancyChart from "lib/components/common/charts/room-occupancy-chart";
import RoomRevenueChart from "lib/components/common/charts/room-revenue-chart";
import ClinicsDropdown from "lib/components/common/dashboard/clinics-dropdown";
import FlexBox from "lib/components/common/flex-box";
import SimpleCard from "lib/components/common/simple-card";
import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { AppContext } from "lib/contexts/app-context";
import useAnalytics from "lib/hooks/useAnalytics";
import useClinics from "lib/hooks/useClinics";
import { formatRetentionGraphData } from "lib/utils/graph-utils";
import { isSuperAdminUser } from "lib/utils/util";
import { useContext, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { PiArrowClockwiseLight } from "react-icons/pi";

const BreadcrumbItems = [
  {
    label: "Reports",
    path: PAGE_ROUTES.ADMIN.Reports,
  },
] as BreadcrumbItem[];

const AdminReportsPage = () => {
  const { appState } = useContext(AppContext);
  const isSuperAdmin = isSuperAdminUser(appState?.userRole);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [roomRevenueEntry, setRoomRevenueEntry] = useState<RoomRevenueEntry[]>(
    []
  );
  const [practitionerRetentionEntry, setPractitionerRetentionEntry] = useState<
    PractitionerRetentionEntry[]
  >([]);
  const [practitionerRevenueEntry, setPractitionerRevenueEntry] = useState<
    PractitionerRevenueEntry[]
  >([]);
  const [occupancyEntry, setOccupancyEntry] = useState<RoomOccupancyEntry[]>(
    []
  );
  const {
    getClinicOccupancyGraphData,
    clinicOccupancyGraphLoading,
    getClinicRoomRevenueGraphData,
    clinicRevenueGraphLoading,
    getClinicPractitionersRevenueGraphData,
    clinicPractitionersRevenueGraphLoading,
    getClinicPractitionerRetentionGraphData,
    clinicPractitionerRetentionGraphLoading,
    error: analyticsError,
  } = useAnalytics();

  const {
    getClinics,
    clinics,
    loading: clinicsLoading,
    error: clinicsError,
  } = useClinics();

  useEffect(() => {
    getClinics(isSuperAdmin, handleClinicsLoaded);
  }, []);

  useEffect(() => {
    if (selectedClinic) {
      getClinicOccupancyGraphData(selectedClinic?.id ?? "", setOccupancyEntry);
      getClinicRoomRevenueGraphData(
        selectedClinic?.id ?? "",
        setRoomRevenueEntry
      );
      getClinicPractitionersRevenueGraphData(
        selectedClinic?.id ?? "",
        setPractitionerRevenueEntry
      );
      getClinicPractitionerRetentionGraphData(
        selectedClinic?.id ?? "",
        setPractitionerRetentionEntry
      );
    }
  }, [selectedClinic]);

  const handleClinicsLoaded = (clinics: Clinic[]) => {
    if (clinics?.length > 0) {
      setSelectedClinic(clinics[0]);
    }
  };
  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <SimpleCard
        title="Reports"
        error={analyticsError || clinicsError}
        isLoading={clinicsLoading}
        action={
          <ClinicsDropdown
            clinics={clinics ?? []}
            onSelect={(clinic) => {
              setSelectedClinic(clinic);
            }}
            selectedClinic={selectedClinic}
          />
        }
      >
        <Row>
          <Col sm={12}>
            <SimpleCard
              isLoading={clinicOccupancyGraphLoading}
              className="w-100 mb-3"
            >
              <FlexBox
                className="border-bottom"
                justifyContent="between"
                alignItems="center"
              >
                <h5>Clinic Occupancy</h5>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    getClinicOccupancyGraphData(
                      selectedClinic?.id ?? "",
                      setOccupancyEntry
                    );
                  }}
                >
                  <PiArrowClockwiseLight />
                </Button>
              </FlexBox>
              <RoomOccupancyChart
                roomDetails={occupancyEntry}
                height={200}
                isShowAllMonths
              />
            </SimpleCard>
            <SimpleCard
              className="w-100 mb-3"
              isLoading={clinicPractitionersRevenueGraphLoading}
            >
              <FlexBox
                className="border-bottom"
                justifyContent="between"
                alignItems="center"
              >
                <h5>Practitioner Revenue</h5>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    getClinicPractitionersRevenueGraphData(
                      selectedClinic?.id ?? "",
                      setPractitionerRevenueEntry
                    );
                  }}
                >
                  <PiArrowClockwiseLight />
                </Button>
              </FlexBox>
              <PractitionerRevenueChart
                data={practitionerRevenueEntry}
                height={300}
              />
            </SimpleCard>
            <SimpleCard
              className="w-100 mb-3"
              isLoading={clinicRevenueGraphLoading}
            >
              <FlexBox
                className="border-bottom"
                justifyContent="between"
                alignItems="center"
              >
                <h5>Rooms Revenue</h5>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    getClinicRoomRevenueGraphData(
                      selectedClinic?.id ?? "",
                      setRoomRevenueEntry
                    );
                  }}
                >
                  <PiArrowClockwiseLight />
                </Button>
              </FlexBox>
              <RoomRevenueChart data={roomRevenueEntry} height={300} />
            </SimpleCard>
            <SimpleCard className="w-100 mb-3" isLoading={clinicPractitionerRetentionGraphLoading}>
              <FlexBox
                className="border-bottom"
                justifyContent="between"
                alignItems="center"
              >
                <h5>Practitioner Retention Rate</h5>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    getClinicPractitionerRetentionGraphData(
                      selectedClinic?.id ?? "",
                      setPractitionerRetentionEntry
                    );
                  }}
                >
                  <PiArrowClockwiseLight />
                </Button>
              </FlexBox>
              <GroupedBarChart
                data={formatRetentionGraphData(
                  practitionerRetentionEntry,
                  selectedClinic?.name ?? ""
                )}
                height={400}
                width={"100%"}
              />
            </SimpleCard>
          </Col>
        </Row>
      </SimpleCard>
    </DashboardLayout>
  );
};

export default AdminReportsPage;
